import React from 'react';
import { Box, Container, Typography, Divider, Link } from '@mui/material';

const CONTACT_EMAIL = 'support@amit-klein.com';
const APP_URL = 'https://media.amit-klein.com';
const LAST_UPDATED = 'March 22, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom fontWeight={600}>
      {title}
    </Typography>
    {children}
  </Box>
);

const TermsOfServicePage = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Terms of Service
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Last updated: {LAST_UPDATED}
    </Typography>
    <Divider sx={{ my: 3 }} />

    <Section title="1. Acceptance of Terms">
      <Typography variant="body1" paragraph>
        By accessing or using Amit Klein Media ({APP_URL}) ("the App"), you agree to be bound
        by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not
        use the App.
      </Typography>
    </Section>

    <Section title="2. Description of Service">
      <Typography variant="body1" paragraph>
        The App is a private media album management platform that allows invited users to
        upload, organise, and analyse photos and videos using AI-powered features including
        face recognition and image analysis.
      </Typography>
    </Section>

    <Section title="3. Eligibility & Access">
      <Typography variant="body1" paragraph>
        The App is open to anyone. You must be at least 13 years old to use the App.
      </Typography>
      <Typography variant="body1" paragraph>
        Some features — such as importing photos directly from Google — require a Google
        account. By connecting a Google account you also agree to{' '}
        <Link href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
          Google's Terms of Service
        </Link>.
      </Typography>
    </Section>

    <Section title="4. User Responsibilities">
      <Typography component="ul" sx={{ pl: 3 }}>
        {[
          'You are responsible for all content you upload to the App.',
          'You must not upload content that is illegal, infringing, defamatory, or otherwise harmful.',
          'You must not attempt to circumvent access controls or use the App in ways that could harm other users or the service.',
          'You must not reverse-engineer, scrape, or redistribute any part of the App or its AI outputs.',
          'You are responsible for keeping your Google account credentials secure.',
        ].map((item) => (
          <Typography key={item} component="li" variant="body1" sx={{ mb: 1 }}>
            {item}
          </Typography>
        ))}
      </Typography>
    </Section>

    <Section title="5. Intellectual Property">
      <Typography variant="body1" paragraph>
        You retain ownership of all photos and videos you upload. By uploading content you grant
        us a limited, non-exclusive licence to store and process that content solely for the
        purpose of providing the service to you (including running AI analysis you request).
      </Typography>
      <Typography variant="body1" paragraph>
        All software, design, trademarks, and other materials comprising the App are owned by
        or licenced to Amit Klein and may not be copied or reused without prior written
        permission.
      </Typography>
    </Section>

    <Section title="6. AI-Powered Features">
      <Typography variant="body1" paragraph>
        The App uses third-party AI services (AWS Rekognition, OpenAI / Azure OpenAI) to
        process uploaded photos. Results are provided as-is and may not be perfectly accurate.
        You should not rely solely on AI-generated outputs for any critical decision.
      </Typography>
    </Section>

    <Section title="7. Privacy">
      <Typography variant="body1" paragraph>
        Your use of the App is also governed by our{' '}
        <Link href="/privacy-policy">Privacy Policy</Link>, which is incorporated into these
        Terms by reference.
      </Typography>
    </Section>

    <Section title="8. Disclaimer of Warranties">
      <Typography variant="body1" paragraph>
        The App is provided "as is" and "as available" without warranties of any kind, express
        or implied, including but not limited to warranties of merchantability, fitness for a
        particular purpose, or non-infringement. We do not warrant that the App will be
        uninterrupted, error-free, or free of viruses or other harmful components.
      </Typography>
    </Section>

    <Section title="9. Limitation of Liability">
      <Typography variant="body1" paragraph>
        To the fullest extent permitted by applicable law, Amit Klein shall not be liable for
        any indirect, incidental, special, consequential, or punitive damages arising out of
        or relating to your use of the App, even if advised of the possibility of such damages.
        Our total liability for any claim arising from these Terms or your use of the App shall
        not exceed the amount you paid to use the App in the twelve months preceding the claim
        (or €50 if no amount was paid).
      </Typography>
    </Section>

    <Section title="10. Termination">
      <Typography variant="body1" paragraph>
        We reserve the right to suspend or terminate your access to the App at any time, with
        or without notice, for any violation of these Terms or for any other reason at our
        sole discretion. You may stop using the App at any time.
      </Typography>
    </Section>

    <Section title="11. Changes to These Terms">
      <Typography variant="body1" paragraph>
        We may update these Terms from time to time. The "Last updated" date at the top of
        this page will reflect any changes. Continued use of the App after updated Terms are
        posted constitutes your acceptance of the revised Terms.
      </Typography>
    </Section>

    <Section title="12. Governing Law">
      <Typography variant="body1" paragraph>
        These Terms are governed by the laws of Israel, without regard to its conflict of
        law provisions. Any disputes arising under these Terms shall be subject to the
        exclusive jurisdiction of the courts of Israel.
      </Typography>
    </Section>

    <Section title="13. Contact Us">
      <Typography variant="body1">
        If you have questions about these Terms of Service, please contact us:
      </Typography>
      <Typography variant="body1" sx={{ mt: 1 }}>
        <strong>Email:</strong>{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
      </Typography>
      <Typography variant="body1" sx={{ mt: 1 }}>
        <strong>Website:</strong>{' '}
        <Link href={APP_URL}>{APP_URL}</Link>
      </Typography>
    </Section>
  </Container>
);

export default TermsOfServicePage;
